/**
 * @Copyright (c) Microsoft Corporation.  All rights reserved.
 *
 * @file DeveloperToolsLoader.ts
 */

import DeveloperTools from './DeveloperTools';
import { IDeveloperToolsTab } from './IDeveloperToolsTab';
import ModuleStore from './../stores/ModuleStore';

interface IDeveloperToolsWrapper {
  default: typeof DeveloperTools;
}

// Ensure the load-themed-styles loader is in this chunk
require('@microsoft/load-themed-styles');

export default class DeveloperToolsLoader {
  private static _instance: DeveloperToolsLoader;

  public static get instance(): DeveloperToolsLoader {
    if (!DeveloperToolsLoader._instance) {
      DeveloperToolsLoader._instance = new DeveloperToolsLoader();
    }

    return DeveloperToolsLoader._instance;
  }

  private _developerTools: DeveloperTools;

  private _developerToolsTabsBacklog: IDeveloperToolsTab[] = [];

  /**
   * Registers a tab in the developer tools. The developer tools are invoked by pressing "CTRL+SHIFT+~".
   *
   * @param {developerToolsTab} The tab definition.
   *
   * @internal
   */
  public registerDeveloperToolsTab(developerToolsTab: IDeveloperToolsTab): void {
    if (this._developerTools) {
      this._developerTools.registerDeveloperToolsTab(developerToolsTab);
    } else {
      this._developerToolsTabsBacklog.push(developerToolsTab);
    }
  }

  /**
   * Shows/hides the developer tools panel.
   *
   * @internal
   */
  public toggleDeveloperTools(): void { // tslint:disable-line:export-name
    if (this._developerTools) {
      this._developerTools.toggleDeveloperTools();
    } else {
      // If we're building an NPM module, we need to make sure React, Flux, and React-DOM aren't bundled. Instead,
      // we'll load them from node_modules.
      if (NPM_BUILD) {
        // Ensure the fake react, flux, and react-dom modules are included in this chunk
        require.include('react');
        require.include('flux');
        require.include('react-dom');

        /* tslint:disable:no-any */
        const reactPromise: Promise<any> = ModuleStore.instance.npmLoadPackage('react');
        const fluxPromise: Promise<any> = ModuleStore.instance.npmLoadPackage('flux');
        const reactDomPromise: Promise<any> = ModuleStore.instance.npmLoadPackage('react-dom');
        /* tslint:enable:no-any */

        Promise.all([reactPromise, reactDomPromise, fluxPromise])
          .then((results: any[]) => { // tslint:disable-line:no-any
            /* tslint:disable:typedef */
            const react = results[0];
            const reactDom = results[1];
            const flux = results[2];
            /* tslint:enable:typedef */

            this._injectReactFluxIntoCache(react, reactDom, flux);

            this._loadDeveloperTools(!this._tryFillDevToolsFabric());
          });
      } else if (this._tryFillReactFlux()) {
        this._loadDeveloperTools(!this._tryFillDevToolsFabric());
      } else {
        require.ensure(['react', 'react-dom', 'flux'], () => {
          require.ensure(['./DeveloperToolsFabric'], () => {
            require.ensure(['./DeveloperTools'], (require: ((path: string) => IDeveloperToolsWrapper)) => {
              this._initializeDeveloperTools(require('./DeveloperTools').default);
            }, 'developer-tools');
          }, 'developer-tools-fabric');
        }, 'react-flux');
      }
    }
  }

  private _tryFillReactFlux(): boolean {
    /* tslint:disable:typedef */
    const react = ModuleStore.instance.getModuleById('react');
    const reactDom = ModuleStore.instance.getModuleById('react-dom');
    const flux = ModuleStore.instance.getModuleById('flux');
    /* tslint:enable:typedef */

    if (react && flux && reactDom) {
      this._injectReactFluxIntoCache(react, reactDom, flux);
      return true;
    } else {
      return false;
    }
  }

  private _tryFillDevToolsFabric(): boolean {
    const fabric = ModuleStore.instance.getModuleById('office-ui-fabric-react'); // tslint:disable-line:typedef

    if (fabric) {
      const fabricId: number = require.resolveWeak('./DeveloperToolsFabric');
      if (!require.cache[fabricId]) {
        require.cache[fabricId] = {
          exports: fabric
        };
      } else {
        require.cache[fabricId].exports = fabric;
      }

      return true;
    } else {
      return false;
    }
  }

  private _injectReactFluxIntoCache(react, reactDom, flux): void { // tslint:disable-line:typedef
    const reactId: number = require.resolveWeak('react');
    const reactDomId: number = require.resolveWeak('react-dom');
    const fluxId: number = require.resolveWeak('flux');

    // Inject react, react-dom, and flux into the module cache
    if (!require.cache[reactId]) {
      require.cache[reactId] = {
        exports: react
      };
    } else {
      require.cache[reactId].exports = react;
    }

    if (!require.cache[reactDomId]) {
      require.cache[reactDomId] = {
        exports: reactDom
      };
    } else {
      require.cache[reactDomId].exports = reactDom;
    }

    if (!require.cache[fluxId]) {
      require.cache[fluxId] = {
        exports: flux
      };
    } else {
      require.cache[fluxId].exports = flux;
    }
  }

  private _loadDeveloperTools(loadFabric: boolean): void {
    const loadDeveloperTools: () => void = () => {
      __webpack_chunk_load__(/* developer-tools */3, (requireFn: ((id: number) => IDeveloperToolsWrapper)) => {
        this._initializeDeveloperTools(requireFn(require.resolveWeak('./DeveloperTools')).default);
      });
    };

    if (loadFabric) {
      __webpack_chunk_load__(/* developer-tools-fabric */2, loadDeveloperTools);
    } else {
      loadDeveloperTools();
    }
  }

  private _initializeDeveloperTools(developerToolsClass: typeof DeveloperTools): void  {
    this._developerTools = developerToolsClass.instance;
    this._developerTools.initialize();

    this._developerToolsTabsBacklog.forEach((developerToolsTab: IDeveloperToolsTab) => {
      this._developerTools.registerDeveloperToolsTab(developerToolsTab);
    });

    this._developerTools.showHideDeveloperTools(true);
  }
}


// WEBPACK FOOTER //
// /src/DeveloperTools/DeveloperToolsLoader.ts


// WEBPACK FOOTER //
// webpack:///src/DeveloperTools/DeveloperToolsLoader.ts